import { sha256 } from "./canonical.js";
import { latestDependencyEdges } from "./dependency.js";
const pqSignatures = new Set([
    "ML-DSA-65",
    "SLH-DSA-SHA2-128s",
    "HYBRID-ECDSA-P384-ML-DSA-65",
]);
function uniqueSorted(values) {
    return [...new Set(values)].sort();
}
function cryptoShape(policy) {
    return {
        signatures: policy.signatures,
        key_establishment: policy.key_establishment,
        workload_profiles: policy.workload_profiles,
        ...(policy.issuance_guardrails ? { issuance_guardrails: policy.issuance_guardrails } : {}),
        transition: policy.transition ?? { mode: "classical" },
    };
}
function prefixMatches(endpointID, profile) {
    return profile.allowed_spiffe_prefixes.some((prefix) => prefix.endsWith("/") && endpointID.startsWith(prefix));
}
function latestCapabilities(records) {
    const result = new Map();
    for (const record of [...records].sort((left, right) => {
        const endpoint = left.endpoint_id.localeCompare(right.endpoint_id);
        if (endpoint)
            return endpoint;
        const observed = Date.parse(right.observed_at) - Date.parse(left.observed_at);
        return observed || right.id.localeCompare(left.id);
    })) {
        if (!result.has(record.endpoint_id))
            result.set(record.endpoint_id, record);
    }
    return result;
}
function classifyEndpoint(endpointID, configuredProfiles, policy, capability, now, platformPolicy) {
    const profileEntries = Object.entries(policy.workload_profiles);
    const inferredProfiles = profileEntries
        .filter(([, profile]) => prefixMatches(endpointID, profile))
        .map(([profileID]) => profileID);
    const profileIDs = uniqueSorted(configuredProfiles.length ? configuredProfiles : inferredProfiles);
    const reasons = [];
    let status = "compatible";
    if (!capability) {
        status = "missing";
        reasons.push("No capability evidence exists for this managed endpoint");
    }
    else if (Date.parse(capability.expires_at) <= now.getTime()) {
        status = "stale";
        reasons.push("The latest capability evidence is expired");
    }
    else if (capability.source === "import") {
        status = "unverified";
        reasons.push("Imported claims are not machine-observed migration evidence");
    }
    const unknownProfiles = profileIDs.filter((profileID) => !policy.workload_profiles[profileID]);
    const scopeMismatches = profileIDs.filter((profileID) => {
        const profile = policy.workload_profiles[profileID];
        return profile ? !prefixMatches(endpointID, profile) : false;
    });
    if (!profileIDs.length || unknownProfiles.length || scopeMismatches.length) {
        status = "unassigned";
        if (!profileIDs.length)
            reasons.push("The endpoint is not assigned to a workload profile");
        if (unknownProfiles.length)
            reasons.push(`Assigned profiles are absent from the proposal: ${unknownProfiles.join(", ")}`);
        if (scopeMismatches.length)
            reasons.push(`Endpoint identity is outside assigned profile scope: ${scopeMismatches.join(", ")}`);
    }
    else if (profileIDs.length > 1) {
        status = "ambiguous";
        reasons.push(`Endpoint identity maps to multiple workload profiles: ${profileIDs.join(", ")}`);
    }
    if (capability && status === "compatible") {
        const profile = policy.workload_profiles[profileIDs[0]];
        if (!profile) {
            status = "unassigned";
            reasons.push("The assigned workload profile is absent from the proposal");
        }
        else {
            const policyAlgorithms = policy.signatures.allowed.filter((algorithm) => !policy.signatures.forbidden.includes(algorithm));
            const allowedAlgorithms = profile.allowed_algorithms.filter((algorithm) => policyAlgorithms.includes(algorithm) &&
                (!platformPolicy || (platformPolicy.signatures.allowed.includes(algorithm) && !platformPolicy.signatures.forbidden.includes(algorithm))));
            if (!capability.signature_algorithms.some((algorithm) => allowedAlgorithms.includes(algorithm))) {
                status = "incompatible";
                reasons.push("No measured signature algorithm remains allowed by the proposed profile and policy");
            }
            const allowedKeyEstablishment = [...new Set([
                    ...policy.key_establishment.preferred,
                    ...policy.key_establishment.allowed,
                ])].sort().filter((algorithm) => !platformPolicy || platformPolicy.key_establishment.allowed.includes(algorithm));
            if (!capability.key_establishment.some((algorithm) => allowedKeyEstablishment.includes(algorithm))) {
                status = "incompatible";
                reasons.push("No measured key-establishment mode remains allowed by the proposed policy");
            }
            if ((policy.transition?.mode === "hybrid" || policy.transition?.mode === "pqc") &&
                !capability.key_establishment.includes("X25519MLKEM768")) {
                status = "incompatible";
                reasons.push("Hybrid or PQC transition requires measured X25519MLKEM768 support");
            }
            if (policy.transition?.mode === "pqc" &&
                !capability.signature_algorithms.some((algorithm) => pqSignatures.has(algorithm))) {
                status = "incompatible";
                reasons.push("PQC transition requires a measured post-quantum signature capability");
            }
        }
    }
    return {
        endpoint_id: endpointID,
        profile_ids: profileIDs,
        status,
        reasons: uniqueSorted(reasons),
        ...(capability
            ? {
                capability_id: capability.id,
                capability_sha256: capability.evidence_sha256,
                capability_source: capability.source,
                observed_at: capability.observed_at,
                expires_at: capability.expires_at,
            }
            : {}),
    };
}
export async function assessPolicyImpact(input) {
    const maximumEndpoints = input.maximumEndpoints ?? 25_000;
    if (!Number.isSafeInteger(maximumEndpoints) || maximumEndpoints < 1 || maximumEndpoints > 100_000) {
        throw new TypeError("invalid policy impact endpoint limit");
    }
    if ((input.dependencies?.length ?? 0) > maximumEndpoints * 4) {
        throw new RangeError(`policy dependency evidence exceeds ${maximumEndpoints * 4} observations`);
    }
    const latest = latestCapabilities(input.capabilities);
    const configured = new Map();
    for (const endpoint of input.managedEndpoints) {
        if (!endpoint.endpoint_id || endpoint.endpoint_id.length > 256)
            throw new TypeError("invalid managed endpoint identity");
        const profiles = configured.get(endpoint.endpoint_id) ?? new Set();
        for (const profile of endpoint.profile_ids ?? [])
            profiles.add(profile);
        configured.set(endpoint.endpoint_id, profiles);
    }
    const dependencyGraph = latestDependencyEdges(input.dependencies ?? []);
    if (dependencyGraph.length > maximumEndpoints) {
        throw new RangeError(`policy dependency graph exceeds ${maximumEndpoints} latest edges`);
    }
    for (const edge of dependencyGraph) {
        if (edge.tenant_id !== input.policy.tenant_id)
            throw new TypeError("cross-tenant policy dependency evidence is forbidden");
    }
    const liveDependencies = dependencyGraph.filter((edge) => Date.parse(edge.expires_at) > input.now.getTime());
    const liveDependencyEndpoints = uniqueSorted(liveDependencies.flatMap((edge) => [edge.source_endpoint_id, edge.target_endpoint_id]));
    if (input.requireDependencyEvidence) {
        for (const endpointID of liveDependencyEndpoints) {
            if (!configured.has(endpointID))
                configured.set(endpointID, new Set());
        }
    }
    if (configured.size > maximumEndpoints) {
        throw new RangeError(`policy impact inventory exceeds ${maximumEndpoints} managed endpoints`);
    }
    const allEndpoints = [...configured.entries()]
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([endpointID, profileIDs]) => classifyEndpoint(endpointID, [...profileIDs], input.policy, latest.get(endpointID), input.now, input.platformPolicy));
    const statuses = [
        "compatible", "incompatible", "missing", "stale", "unassigned", "ambiguous", "unverified",
    ];
    const counts = Object.fromEntries(statuses.map((status) => [status, allEndpoints.filter((endpoint) => endpoint.status === status).length]));
    const policySHA256 = await sha256(input.policy);
    const previousPolicySHA256 = await sha256(input.previousPolicy);
    const platformPolicySHA256 = input.platformPolicy ? await sha256(input.platformPolicy) : undefined;
    const cryptographicChange = (await sha256(cryptoShape(input.policy))) !== (await sha256(cryptoShape(input.previousPolicy)));
    const blockingCount = allEndpoints.length - counts.compatible;
    const capabilityValidUntil = allEndpoints
        .map((endpoint) => endpoint.expires_at)
        .filter((value) => Boolean(value) && Date.parse(value) > input.now.getTime())
        .sort()[0];
    const dependencyValidUntil = liveDependencies.map((edge) => edge.expires_at).sort()[0];
    const validUntil = [capabilityValidUntil, dependencyValidUntil].filter((value) => Boolean(value)).sort()[0];
    const maximumDetails = input.maximumDetails ?? 500;
    if (!Number.isSafeInteger(maximumDetails) || maximumDetails < 0 || maximumDetails > 5_000) {
        throw new TypeError("invalid policy impact detail limit");
    }
    const endpoints = [
        ...allEndpoints.filter((endpoint) => endpoint.status !== "compatible"),
        ...allEndpoints.filter((endpoint) => endpoint.status === "compatible"),
    ].slice(0, maximumDetails);
    const endpointCommitmentsSHA256 = await sha256(allEndpoints);
    const dependencySummary = {
        required: input.requireDependencyEvidence === true,
        graph_commitment_sha256: await sha256(dependencyGraph),
        graph_edges: dependencyGraph.length,
        live_edges: liveDependencies.length,
        stale_edges: dependencyGraph.length - liveDependencies.length,
        live_endpoints: liveDependencyEndpoints.length,
        ...(dependencyValidUntil ? { valid_until: dependencyValidUntil } : {}),
    };
    const dependencyBlocking = input.requireDependencyEvidence === true && liveDependencies.length === 0;
    const material = {
        protocol: "sectigo-edge.policy-impact.v1",
        tenant_id: input.policy.tenant_id,
        policy_id: input.policy.id,
        policy_version: input.policy.version,
        policy_sha256: policySHA256,
        ...(input.platformPolicy ? {
            platform_policy_id: input.platformPolicy.id,
            platform_policy_version: input.platformPolicy.version,
            platform_policy_sha256: platformPolicySHA256,
        } : {}),
        previous_policy_sha256: previousPolicySHA256,
        ...(validUntil ? { valid_until: validUntil } : {}),
        cryptographic_change: cryptographicChange,
        blocking: cryptographicChange && (allEndpoints.length === 0 || blockingCount > 0 || dependencyBlocking),
        endpoint_commitments_sha256: endpointCommitmentsSHA256,
        dependency_graph: dependencySummary,
        details_truncated: endpoints.length < allEndpoints.length,
        counts: { total: allEndpoints.length, ...counts },
        endpoints,
    };
    return {
        ...material,
        assessed_at: input.now.toISOString(),
        impact_sha256: await sha256(material),
    };
}
//# sourceMappingURL=policy-impact.js.map